import React, { useEffect, useState, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Sparkles, Ticket, Calendar, Clock, Users, Check, ShieldCheck, AlertCircle } from "lucide-react";
import SEOHead from "../components/SEOHead";
import useSurcharge from "../hooks/useSurcharge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIERS = [
 { id: "cruise-1", name: "Vivid Cruise 1", desc: "Sparkling wine or soft drink on arrival", featured: false },
 { id: "cruise-2", name: "Vivid Cruise 2", desc: "Sparkling wine or soft drink on arrival", featured: false },
 { id: "cruise-3", name: "Vivid Cruise 3", desc: "Spirit, beer, wine or sparkling on arrival", featured: true }];

const fmtMonth = (d) => new Date(d + "T00:00:00").toLocaleDateString("en-AU", { day: "numeric", month: "long" });
const fmtTime = (t) => {
 const [h, m] = t.split(":").map(Number);
 const ap = h >= 12 ? "pm" : "am";
 const hh = ((h + 11) % 12) + 1;
 return `${hh}:${m.toString().padStart(2, "0")}${ap}`;
};

export default function BookVividCruise() {
 const navigate = useNavigate();
 const [params] = useSearchParams();
 const [availability, setAvailability] = useState(null);
 const [loading, setLoading] = useState(true);
 const [step, setStep] = useState(1);
 const [picked, setPicked] = useState({ date: "", time: "", side: "", tier: "cruise-3", adult_qty: 2, child_qty: 0 });
 const [customer, setCustomer] = useState({ name: "", email: "", phone: "" });
 const [submitting, setSubmitting] = useState(false);

 useEffect(() => { document.title = "Book Vivid Cruise Tickets | Magic Cruises"; }, []);

 useEffect(() => {
  if (params.get("cancelled")) toast.info("Payment cancelled, your seats were not booked. Try again when you're ready.");
  const t = params.get("tier");
  if (t && ["cruise-1", "cruise-2", "cruise-3"].includes(t)) setPicked(p => ({ ...p, tier: t }));
 }, [params]);

 useEffect(() => {
  (async () => {
   try {
    const r = await axios.get(`${API}/vivid/availability`);
    setAvailability(r.data);
   } catch (e) {
    toast.error("Could not load live availability. Please refresh.");
   } finally { setLoading(false); }
  })();
 }, []);

 const dateObj = useMemo(() => availability?.dates.find(d => d.date === picked.date), [availability, picked.date]);
 const departureObj = useMemo(() => dateObj?.cruises.find(c => c.time === picked.time && c.side === picked.side), [dateObj, picked.time, picked.side]);
 const totalQty = picked.adult_qty + picked.child_qty;
 const adultPrice = availability?.pricing.adult ?? 59;
 const childPrice = availability?.pricing.child ?? 35;
 const subtotal = (picked.adult_qty * adultPrice) + (picked.child_qty * childPrice);
 const { cfg: surchargeCfg, apply: applySurcharge } = useSurcharge();
 const { surcharge, total } = applySurcharge(subtotal);

 const canNextFrom = {
  1: !!picked.date,
  2: !!picked.time && !!picked.side,
  3: !!picked.tier,
  4: totalQty > 0 && (!departureObj || totalQty <= departureObj.remaining),
  5: customer.name.trim().length >= 2 && /.+@.+\..+/.test(customer.email),
 };

 const startCheckout = async () => {
  if (!canNextFrom[5]) { toast.error("Please complete your name and email"); return; }
  setSubmitting(true);
  try {
   const r = await axios.post(`${API}/checkout/vivid`, {
    date: picked.date, time: picked.time, side: picked.side, tier: picked.tier,
    adult_qty: picked.adult_qty, child_qty: picked.child_qty,
    customer_name: customer.name, customer_email: customer.email, customer_phone: customer.phone,
    origin_url: window.location.origin,
   });
   window.location.href = r.data.url;
  } catch (e) {
   toast.error(e?.response?.data?.detail || "Checkout failed. Please try again.");
   setSubmitting(false);
  }
 };

 const StepDot = ({ n, label }) => (
  <div className="flex items-center gap-2.5 shrink-0">
   <span className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-black ${step === n ? "bg-fuchsia-500 text-white shadow-lg shadow-fuchsia-500/50" : step > n ? "bg-fuchsia-500/30 text-fuchsia-200" : "bg-white/10 text-white/50"}`}>{step > n ? <Check className="w-4 h-4" /> : n}</span>
   <span className={`text-xs uppercase tracking-[0.22em] font-bold hidden sm:inline ${step === n ? "text-white" : "text-white/60"}`}>{label}</span>
  </div>
 );

 return (
  <main className="bg-[hsl(260,55%,4%)] text-white min-h-screen" data-testid="vivid-book-page">
   <SEOHead title="Book Vivid Cruise Tickets | Magic Cruises" description="Buy Vivid Sydney 2027 cruise tickets online. Adult $49, Child $35. 14 dates, multiple departures, instant confirmation." />

   {/* Atmospheric bg */}
   <div className="fixed inset-0 pointer-events-none">
    <div className="absolute top-0 left-1/3 w-[40rem] h-[40rem] rounded-full bg-fuchsia-500/15 blur-3xl animate-pulse" />
    <div className="absolute bottom-0 right-1/4 w-[36rem] h-[36rem] rounded-full bg-violet-500/12 blur-3xl animate-pulse" style={{ animationDelay: "2s" }} />
   </div>

   <div className="relative pt-28 pb-32 px-5 lg:px-10 mx-auto max-w-6xl">
    <Link to="/cruises/vivid-cruises-sydney" className="inline-flex items-center gap-2 text-white/65 hover:text-fuchsia-300 text-xs font-bold uppercase tracking-[0.28em] mb-7 transition-colors" data-testid="book-back-link">
     <ArrowLeft className="w-3.5 h-3.5" /> Back to Vivid Cruises
    </Link>

    <div className="flex items-center gap-2 text-fuchsia-300 text-[11px] uppercase tracking-[0.32em] font-bold mb-4">
     <Ticket className="w-3.5 h-3.5" /> Vivid Sydney 2027 · Tickets
    </div>
    <h1 className="font-display font-black tracking-tight leading-[0.95] mb-10" style={{ fontSize: "clamp(2.5rem, 6vw, 5rem)" }}>
     Book your <span className="vivid-gradient-text italic font-light">Vivid night.</span>
    </h1>

    {/* Step progress bar */}
    <div className="flex items-center gap-3 sm:gap-5 overflow-x-auto pb-3 mb-10 -mx-2 px-2" data-testid="book-steps">
     <StepDot n={1} label="Date" />
     <div className="h-px w-6 sm:w-10 bg-white/15 shrink-0" />
     <StepDot n={2} label="Departure" />
     <div className="h-px w-6 sm:w-10 bg-white/15 shrink-0" />
     <StepDot n={3} label="Tier" />
     <div className="h-px w-6 sm:w-10 bg-white/15 shrink-0" />
     <StepDot n={4} label="Tickets" />
     <div className="h-px w-6 sm:w-10 bg-white/15 shrink-0" />
     <StepDot n={5} label="Pay" />
    </div>

    {loading && <div className="text-white/65 text-center py-20">Loading live availability…</div>}

    {!loading && availability && (
     <div className="grid lg:grid-cols-12 gap-8 lg:gap-12">
      {/* MAIN STEP */}
      <div className="lg:col-span-8">
       {step === 1 && (
        <div data-testid="step-date">
         <h2 className="text-3xl md:text-4xl font-black mb-3">1. Pick your date</h2>
         <p className="text-white/85 text-lg mb-8 leading-[1.7]">14 Vivid sailing nights between 28 May and 19 June 2027. Friday/Saturday sells first.</p>
         <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="date-grid">
          {availability.dates.map(d => {
           const totalRemaining = d.cruises.reduce((s, c) => s + c.remaining, 0);
           const isSoldOut = totalRemaining === 0;
           const isLow = totalRemaining > 0 && totalRemaining <= 30;
           const active = picked.date === d.date;
           const isFinale = d.date === "2027-06-19";
           return (
            <button key={d.date} disabled={isSoldOut} onClick={() => setPicked(p => ({ ...p, date: d.date, time: "", side: "" }))} data-testid={`date-${d.date}`}
             className={`group relative rounded-2xl p-5 lg:p-6 text-left transition-all ${active ? "ring-2 ring-fuchsia-400 bg-gradient-to-br from-fuchsia-600/40 to-violet-600/30 shadow-xl shadow-fuchsia-500/40" : isSoldOut ? "ring-1 ring-white/5 bg-white/[0.02] opacity-50 cursor-not-allowed" : "ring-1 ring-white/10 bg-white/[0.04] hover:ring-fuchsia-400/40 hover:-translate-y-0.5"}`}>
             {isFinale && !isSoldOut && <span className="absolute -top-2.5 right-3 bg-fuchsia-500 text-white text-[10px] uppercase tracking-[0.22em] font-black px-2.5 py-1 rounded-full shadow-md">Finale</span>}
             <div className={`text-4xl lg:text-5xl font-black leading-none tracking-tight ${active || isFinale ? "vivid-gradient-text" : "text-fuchsia-300"}`}>{fmtMonth(d.date).split(" ")[0]}</div>
             <div className="text-white/75 text-xs uppercase tracking-widest font-bold mt-2">{fmtMonth(d.date).split(" ").slice(1).join(" ")} · {new Date(d.date).toLocaleDateString("en-AU", { weekday: "short" })}</div>
             <div className="mt-3 text-sm">
              {isSoldOut ? <span className="text-red-300 font-bold">Sold out</span>
               : isLow ? <span className="text-amber-300 font-bold">Only {totalRemaining} left</span>
               : <span className="text-white/80">{d.cruises.length} departures · {totalRemaining} seats</span>}
             </div>
            </button>
           );
          })}
         </div>
        </div>
       )}

       {step === 2 && (
        <div data-testid="step-departure">
         <h2 className="text-3xl md:text-4xl font-black mb-3">2. Pick a departure</h2>
         <p className="text-white/85 text-lg mb-8 leading-[1.7]">{fmtMonth(picked.date)} 2027. Each cruise is 90 minutes. Side A = Bridge-facing, Side B = Opera House-facing.</p>
         <div className="space-y-3" data-testid="time-list">
          {dateObj?.cruises.map((c, i) => {
           const id = `${c.time}_${c.side}`;
           const active = picked.time === c.time && picked.side === c.side;
           const sold = c.status === "sold_out";
           return (
            <button key={id} disabled={sold} onClick={() => setPicked(p => ({ ...p, time: c.time, side: c.side }))} data-testid={`time-${id}`}
             className={`w-full flex items-center justify-between gap-4 p-6 rounded-2xl text-left transition-all ${active ? "ring-2 ring-fuchsia-400 bg-gradient-to-r from-fuchsia-600/35 to-violet-600/25 shadow-xl shadow-fuchsia-500/30" : sold ? "ring-1 ring-white/5 bg-white/[0.02] opacity-50 cursor-not-allowed" : "ring-1 ring-white/10 bg-white/[0.04] hover:ring-fuchsia-400/40"}`}>
             <div className="flex items-center gap-4">
              <Clock className={`w-6 h-6 ${active ? "text-fuchsia-300" : "text-white/60"}`} />
              <div>
               <div className="text-white text-2xl font-black tracking-tight">{fmtTime(c.time)}</div>
               <div className="text-white/70 text-xs uppercase tracking-widest font-bold mt-1.5">Side {c.side} · 90 min</div>
              </div>
             </div>
             <div className="text-right">
              {sold ? <div className="text-red-300 text-base font-bold">Sold out</div>
               : c.status === "almost_full" ? <div className="text-amber-300 text-base font-bold">{c.remaining} left</div>
               : <div className="text-white/82 text-base font-semibold">{c.remaining} seats</div>}
             </div>
            </button>
           );
          })}
         </div>
        </div>
       )}

       {step === 3 && (
        <div data-testid="step-tier">
         <h2 className="text-3xl md:text-4xl font-black mb-3">3. Pick your cruise tier</h2>
         <p className="text-white/85 text-lg mb-8 leading-[1.7]">Same 90-minute Vivid loop. Different welcome drink on arrival.</p>
         <div className="grid sm:grid-cols-3 gap-3" data-testid="tier-list">
          {TIERS.map(t => {
           const active = picked.tier === t.id;
           return (
            <button key={t.id} onClick={() => setPicked(p => ({ ...p, tier: t.id }))} data-testid={`tier-${t.id}`}
             className={`relative rounded-2xl p-6 text-left transition-all ${active ? "ring-2 ring-fuchsia-400 bg-gradient-to-br from-fuchsia-600/35 to-violet-600/25 shadow-xl shadow-fuchsia-500/30" : "ring-1 ring-white/10 bg-white/[0.04] hover:ring-fuchsia-400/40 hover:-translate-y-0.5"}`}>
             {t.featured && <span className="absolute -top-2.5 right-3 bg-fuchsia-500 text-white text-[10px] uppercase tracking-[0.22em] font-black px-2.5 py-1 rounded-full shadow-md">Most Loved</span>}
             <div className="text-white text-xl font-black">{t.name}</div>
             <div className="text-fuchsia-300 text-3xl font-black mt-3">${adultPrice.toFixed(adultPrice % 1 === 0 ? 0 : 2)} <span className="text-white/55 text-xs uppercase tracking-widest font-bold ml-1">adult</span></div>
             <div className="text-white/82 text-base mt-4 leading-[1.7]">{t.desc}</div>
            </button>
           );
          })}
         </div>
        </div>
       )}

       {step === 4 && (
        <div data-testid="step-tickets">
         <h2 className="text-3xl md:text-4xl font-black mb-3">4. How many tickets?</h2>
         <p className="text-white/85 text-lg mb-8 leading-[1.7]">Up to 20 per booking. For groups over 20 enquire for a <Link to="/cruises/vivid-cruises-sydney#enquiry" className="text-fuchsia-300 underline">private Vivid charter</Link>.</p>
         <div className="space-y-4">
          {[
           { k: "adult_qty", label: "Adult", price: adultPrice, hint: "13+ years" },
           { k: "child_qty", label: "Child", price: childPrice, hint: "4 to 12 years" }].map(row => (
           <div key={row.k} className="flex items-center justify-between gap-4 p-6 rounded-2xl ring-1 ring-white/10 bg-white/[0.04]">
            <div>
             <div className="text-white text-2xl font-black">{row.label}</div>
             <div className="text-white/70 text-sm uppercase tracking-widest font-bold mt-1.5">{row.hint} · ${row.price.toFixed(0)} ea</div>
            </div>
            <div className="flex items-center gap-4" data-testid={`qty-${row.k}`}>
             <button onClick={() => setPicked(p => ({ ...p, [row.k]: Math.max(0, p[row.k] - 1) }))} className="w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white text-2xl font-black transition-colors" data-testid={`qty-${row.k}-minus`}>−</button>
             <span className="w-12 text-center text-white text-3xl font-black tabular-nums">{picked[row.k]}</span>
             <button onClick={() => setPicked(p => ({ ...p, [row.k]: Math.min(20, p[row.k] + 1) }))} className="w-12 h-12 rounded-full bg-fuchsia-500 hover:bg-fuchsia-400 text-white text-2xl font-black transition-colors" data-testid={`qty-${row.k}-plus`}>+</button>
            </div>
           </div>
          ))}
          {departureObj && totalQty > departureObj.remaining && (
           <div className="flex items-start gap-3 p-4 rounded-xl bg-red-500/10 border border-red-400/30 text-red-200 text-base" data-testid="qty-error">
            <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
            <div>Only {departureObj.remaining} seats remain on this departure. Reduce quantity or pick another time.</div>
           </div>
          )}
         </div>
        </div>
       )}

       {step === 5 && (
        <div data-testid="step-customer">
         <h2 className="text-3xl md:text-4xl font-black mb-3">5. Your details</h2>
         <p className="text-white/85 text-lg mb-8 leading-[1.7]">We'll email your tickets and Vivid boarding pass to this address.</p>
         <div className="grid sm:grid-cols-2 gap-5">
          <div className="sm:col-span-2"><label className="text-white/70 text-xs uppercase tracking-widest font-bold">Full name *</label><input data-testid="cust-name" value={customer.name} onChange={e => setCustomer(c => ({ ...c, name: e.target.value }))} className="w-full mt-2 px-5 py-4 rounded-xl bg-white/5 border border-white/15 focus:border-fuchsia-400 focus:bg-white/10 outline-none text-white text-lg" placeholder="Your full name" /></div>
          <div><label className="text-white/70 text-xs uppercase tracking-widest font-bold">Email *</label><input data-testid="cust-email" type="email" value={customer.email} onChange={e => setCustomer(c => ({ ...c, email: e.target.value }))} className="w-full mt-2 px-5 py-4 rounded-xl bg-white/5 border border-white/15 focus:border-fuchsia-400 focus:bg-white/10 outline-none text-white text-lg" placeholder="you@email.com" /></div>
          <div><label className="text-white/70 text-xs uppercase tracking-widest font-bold">Phone</label><input data-testid="cust-phone" value={customer.phone} onChange={e => setCustomer(c => ({ ...c, phone: e.target.value }))} className="w-full mt-2 px-5 py-4 rounded-xl bg-white/5 border border-white/15 focus:border-fuchsia-400 focus:bg-white/10 outline-none text-white text-lg" placeholder="0400…" /></div>
         </div>

         <div className="flex items-center gap-2 mt-8 text-white/82 text-sm">
          <ShieldCheck className="w-4 h-4 text-fuchsia-300" />
          Secure payment via Stripe · Cards in AUD · Tickets emailed instantly after payment
         </div>
        </div>
       )}

       {/* Nav buttons */}
       <div className="flex justify-between gap-4 mt-12 pb-24 lg:pb-0">
        <button disabled={step === 1} onClick={() => setStep(s => Math.max(1, s - 1))} className="px-7 py-5 rounded-full bg-white/5 hover:bg-white/10 text-white text-sm font-black uppercase tracking-[0.22em] disabled:opacity-30 disabled:cursor-not-allowed" data-testid="step-prev">
         <ArrowLeft className="w-4 h-4 inline mr-2" /> Back
        </button>
        {step < 5 ? (
         <button disabled={!canNextFrom[step]} onClick={() => setStep(s => s + 1)} className="px-10 py-5 rounded-full bg-fuchsia-500 hover:bg-fuchsia-400 text-white text-sm font-black uppercase tracking-[0.22em] disabled:opacity-40 disabled:cursor-not-allowed shadow-xl shadow-fuchsia-500/40 transition-all" data-testid="step-next">
          Continue <ArrowRight className="w-4 h-4 inline ml-2" />
         </button>
        ) : (
         <button disabled={!canNextFrom[5] || submitting} onClick={startCheckout} className="px-10 py-5 rounded-full bg-fuchsia-500 hover:bg-fuchsia-400 text-white text-sm font-black uppercase tracking-[0.22em] disabled:opacity-40 disabled:cursor-not-allowed shadow-xl shadow-fuchsia-500/40 transition-all" data-testid="step-checkout">
          {submitting ? "Redirecting to Stripe…" : <>Pay ${total.toFixed(2)} AUD <Sparkles className="w-4 h-4 inline ml-2" /></>}
         </button>
        )}
       </div>
      </div>

      {/* SUMMARY SIDEBAR */}
      <aside className="lg:col-span-4 lg:sticky lg:top-28 self-start" data-testid="book-summary">
       <div className="rounded-3xl ring-1 ring-fuchsia-400/30 bg-gradient-to-br from-fuchsia-600/15 via-violet-600/10 to-sky-600/10 backdrop-blur-md p-7 lg:p-8">
        <div className="text-fuchsia-300 text-xs uppercase tracking-[0.32em] font-bold mb-5">Your Vivid cruise</div>
        <dl className="space-y-4 text-base">
         <Row label="Date" value={picked.date ? fmtMonth(picked.date) + " 2027" : ""} icon={Calendar} />
         <Row label="Departure" value={picked.time ? `${fmtTime(picked.time)} · Side ${picked.side}` : ""} icon={Clock} />
         <Row label="Tier" value={TIERS.find(t => t.id === picked.tier)?.name || ""} icon={Sparkles} />
         <Row label="Tickets" value={totalQty > 0 ? `${picked.adult_qty} adult · ${picked.child_qty} child` : ""} icon={Users} />
        </dl>
        <div className="border-t border-white/15 mt-6 pt-6 space-y-3">
         <div className="flex justify-between items-baseline text-white/85 text-base">
          <span><span className="font-black text-white">{picked.adult_qty}</span> × Adult <span className="text-white/50 text-sm">(${adultPrice.toFixed(0)} ea)</span></span>
          <span className="text-lg font-bold tabular-nums">${(picked.adult_qty * adultPrice).toFixed(2)}</span>
         </div>
         <div className="flex justify-between items-baseline text-white/85 text-base">
          <span><span className="font-black text-white">{picked.child_qty}</span> × Child <span className="text-white/50 text-sm">(${childPrice.toFixed(0)} ea)</span></span>
          <span className="text-lg font-bold tabular-nums">${(picked.child_qty * childPrice).toFixed(2)}</span>
         </div>
         {surchargeCfg.enabled && surcharge > 0 && (
          <div className="flex justify-between items-baseline pt-3 mt-2 border-t border-white/10 text-white/75 text-sm">
           <span>{surchargeCfg.label}</span>
           <span className="font-bold tabular-nums">+${surcharge.toFixed(2)}</span>
          </div>
         )}
         <div className="flex justify-between items-baseline pt-4 mt-3 border-t border-white/15">
          <span className="text-white text-2xl font-black">Total AUD</span>
          <span className="vivid-gradient-text text-4xl font-black tabular-nums">${total.toFixed(2)}</span>
         </div>
         <div className="text-white/55 text-[11px] uppercase tracking-widest font-bold pt-1">GST inclusive · Includes card surcharge</div>
        </div>
        <ul className="mt-7 space-y-2.5 text-white/82 text-sm">
         <li className="flex items-start gap-2.5"><Check className="w-4 h-4 text-fuchsia-300 shrink-0 mt-0.5" /> Instant ticket email after payment</li>
         <li className="flex items-start gap-2.5"><Check className="w-4 h-4 text-fuchsia-300 shrink-0 mt-0.5" /> Boards King St Wharf · 15 min early</li>
         <li className="flex items-start gap-2.5"><Check className="w-4 h-4 text-fuchsia-300 shrink-0 mt-0.5" /> Full bar service & climate-controlled deck</li>
        </ul>
       </div>
      </aside>
     </div>
    )}
   </div>

   {/* Sticky bottom CTA bar , always in view, both mobile and desktop */}
   <div className="fixed bottom-0 inset-x-0 z-40 bg-[hsl(270,55%,8%)]/95 backdrop-blur-xl border-t border-fuchsia-400/25 shadow-[0_-15px_45px_-10px_rgba(0,0,0,0.7)]" data-testid="sticky-cta-bar">
    <div className="mx-auto max-w-6xl px-4 py-3 flex items-center gap-3">
     <div className="hidden sm:block flex-1 min-w-0">
      <div className="text-fuchsia-300 text-[10px] uppercase tracking-[0.28em] font-bold">Step {step} of 5{step < 5 ? "" : " · Final"}</div>
      <div className="text-white text-sm font-bold truncate">
       {step === 1 && "Pick your Vivid date"}
       {step === 2 && "Pick your departure time"}
       {step === 3 && "Choose your tier"}
       {step === 4 && "Choose ticket quantity"}
       {step === 5 && `Pay $${total.toFixed(2)} AUD · ${totalQty} ticket${totalQty !== 1 ? "s" : ""}`}
      </div>
     </div>
     <div className="sm:hidden flex-1 min-w-0">
      <div className="text-fuchsia-300 text-[10px] uppercase tracking-[0.28em] font-bold">Step {step}/5</div>
      <div className="text-white text-xs font-bold truncate">{step === 5 ? `$${total.toFixed(2)} · ${totalQty} ticket${totalQty !== 1 ? "s" : ""}` : "Vivid 2027"}</div>
     </div>
     <button disabled={step === 1} onClick={() => setStep(s => Math.max(1, s - 1))} className="px-4 py-3 rounded-full bg-white/8 hover:bg-white/15 text-white text-xs font-black uppercase tracking-[0.22em] disabled:opacity-30 disabled:cursor-not-allowed transition-colors whitespace-nowrap" data-testid="sticky-prev">
      <ArrowLeft className="w-3.5 h-3.5 inline sm:mr-1.5" /><span className="hidden sm:inline">Back</span>
     </button>
     {step < 5 ? (
      <button disabled={!canNextFrom[step]} onClick={() => setStep(s => s + 1)} className="px-6 sm:px-8 py-3 rounded-full bg-fuchsia-500 hover:bg-fuchsia-400 text-white text-xs font-black uppercase tracking-[0.22em] disabled:opacity-40 disabled:cursor-not-allowed shadow-lg shadow-fuchsia-500/40 transition-all whitespace-nowrap" data-testid="sticky-next">
       Continue <ArrowRight className="w-3.5 h-3.5 inline ml-1.5" />
      </button>
     ) : (
      <button disabled={!canNextFrom[5] || submitting} onClick={startCheckout} className="px-6 sm:px-8 py-3 rounded-full bg-fuchsia-500 hover:bg-fuchsia-400 text-white text-xs font-black uppercase tracking-[0.22em] disabled:opacity-40 disabled:cursor-not-allowed shadow-lg shadow-fuchsia-500/40 transition-all whitespace-nowrap" data-testid="sticky-checkout">
       {submitting ? "Redirecting…" : <>Pay ${total.toFixed(2)} <Sparkles className="w-3.5 h-3.5 inline ml-1.5" /></>}
      </button>
     )}
    </div>
   </div>
  </main>
 );
}

function Row({ label, value, icon: Icon }) {
 return (
  <div className="flex items-start gap-3">
   <Icon className="w-5 h-5 text-fuchsia-300 mt-0.5 shrink-0" />
   <div className="flex-1 flex justify-between items-baseline gap-3 flex-wrap">
    <span className="text-white/60 uppercase tracking-widest text-[11px] font-bold">{label}</span>
    <span className="text-white text-right font-bold text-base">{value}</span>
   </div>
  </div>
 );
}
